import type { GPTModelItem } from "@/api/models"

/**
 * Fold a model id to a comparison-stable form.
 *
 * Our model ids and deployments are canonically the DOT form (e.g.
 * `gpt-5.4_eu`), and that is the id a chat response reports. A misconfigured or
 * duplicate registry entry can instead expose the HYPHEN form (`gpt-5-4_eu`)
 * through `/api/config` — the same model with a bad separator. Folding `.` to
 * `-` lets the canonical (dotted) response id still match such a stray entry so
 * the UI degrades gracefully instead of showing a blank model name. The hyphen
 * form is NOT a valid id — this tolerates the misconfig, it does not bless it.
 *
 * Region/variant suffixes (`_eu`, `_global`, …) are left untouched so genuinely
 * distinct deployments (e.g. `gpt-5.4_eu` vs `gpt-5.4_global`) never collapse.
 */
export function foldModelId(id: string): string {
  return id.replace(/\./g, "-")
}

/**
 * Find a configured model by id, tolerant of a `.`->`-` config typo between a
 * response-reported (canonical, dotted) id and a stray hyphen entry that a
 * misconfigured registry can leak into `/api/config`.
 *
 * Use this anywhere a model id sourced from outside the config list (a chat
 * response, a deployment name, a persisted override) is matched against the
 * available-models list. Pickers that select from the list itself don't need
 * it — their ids already match exactly.
 *
 * Falls back to matching on `display_name` to preserve prior badge behaviour.
 */
export function findModelById(
  models: readonly GPTModelItem[],
  id: string
): GPTModelItem | undefined {
  if (!id) return undefined
  const folded = foldModelId(id)
  return models.find(
    (model) => model.id === id || foldModelId(model.id) === folded || model.display_name === id
  )
}
